import { InjectionToken } from '@angular/core';
import { IDfConfig } from '@df/core/df-core.config';
import { TAclRule } from '@df/core/service/acl';
import { ISizeName } from '@df/core/service/client';
import { IPathRule } from '@df/core/service/url';
import { Nexus } from '@tomandco/nexus-api-types';

/**
 * Extend app.config with module properties
 * @see config/df.config.twig.part
 */
declare module '@df/core/df-core.config' {
    interface IDfConfig {
        name: string;
        environment: 'production' | 'uat' | 'staging' | 'development';
        ms: {
            siteId: keyof ISites;
            store: number;
            sites: ISites;
        };
        prerender?: {
            crawlerUserAgent?: string[];
        };
    }
}
export type ISites = Record<string, ISite>;

export type TNumberPerSize = [number, number, number, number];

export type TStringPerSize = [string, string, string, string];

/**
 * Site data.
 *
 * @export
 * @interface ISite
 */
export interface ISite {
    store: number;
    base?: string;
    domain?: string;
    api: string;
    default?: true;
    lang?: string[];
    langGroup?: string; // use to isolate groups of store - only groups with the same value are used in alternates
    contentLang?: string;
    siteName?: string;
    countries?: string[];
    rtl?: boolean; // right-to-left copies
    acl?: TAclRule;
    storePickupCountry?: string;
}

export namespace NCore {
    export const DEFAULT_OPTIONS: NCore.IModuleOptions = {
        breadcrumbs: {
            skipLastCrumb: false
        },
        multisite: {
            useCookie: false
        },
        breakpoints: {
            S: {
                width: 0,
                cmsWidth: 375,
                cmsHeight: 667
            },
            M: {
                width: 701,
                cmsWidth: 768,
                cmsHeight: 1024
            },
            L: {
                width: 1101,
                cmsWidth: 1440,
                cmsHeight: 900
            },
            X: {
                width: 1501,
                cmsWidth: 1920,
                cmsHeight: 1080
            }
        },
        url: {
            pathRules: []
        },
        meta: {
            searchParamsForNoIndex: ['email', 'password']
        }
    };

    export const OPTIONS = new InjectionToken<IModuleOptions>('DF_CORE_OPTIONS', {
        providedIn: 'root',
        factory: () => DEFAULT_OPTIONS
    });

    export interface IModuleOptions {
        breadcrumbs: {
            /**
             * If the last crumb (self) shoould be skipped
             */
            skipLastCrumb: boolean;
            /**
             * Homepage title
             */
            homeTitle?: string;
            /**
             * Product mode
             * defaults to current model
             */
            productMode?: 'current' | 'parent';
            /**
             * Ignore entry page
             * To skip logic for checking the entry page
             */
            ignoreEntryPage?: boolean;
        };
        multisite: {
            useCookie: boolean;
        };
        url: {
            pathRules: IPathRule[];
        };
        structuralData?: {
            useProductGroup?: boolean;
            /**
             * Set it you want to disable listing tracking
             * (sending events with ItemList type)
             */
            noListingTracking?: boolean;
        };
        /**
         * Size breakpoints
         */
        breakpoints: MediaBreakpoint;

        meta: {
            searchParamsForNoIndex: string[];
        };
    }

    export type MediaBreakpoint = Record<
        ISizeName,
        {
            /** minimum screen width */
            width: number;
            /** page width for iCMS */
            cmsWidth?: number;
            /** page height for iCMS */
            cmsHeight?: number;
        }
    >;
}

// global extensions

export interface IAUtoInitData {
    website: Nexus.Lib.General.AppData;
}

declare global {
    interface Window {
        dfConfig: IDfConfig;
        autoInitData?: IAUtoInitData;
    }
}

Error['stackTraceLimit'] = 100;
